import { onConnect, onDisconnect, getSession, disconnect } from '../../../em-shared-session/src/connection';

/**
 * Main Subscribers - Turns subscribers into actual subscriptions
 */
export default class SubscriberProcessor {
    /**
     * Ctor
     */
    constructor(options = {}) {
        // Save passed options
        this.options = options;

        // Subscribers List
        this.subscribersByTopic = {};

        // Subscriptions List
        this.activeSubscriptions = {};

        // Process on Connect / Disconnect
        onConnect(() => this.processSubscribers());
        onDisconnect(() => this.processSubscribers());

        // Next Retry
        this.retryAfterInterval = setInterval(() => this.processSubscribers(), options.retryAfter);

        // Function to Call Each Time Subscriptions List Updates
        this.onSubscriptionsUpdate = activeSubscriptions => activeSubscriptions;

        // Subscribers processing thread
        this.subscriptionsHaveChanged = false;
        setInterval(() => {
            if (this.subscriptionsHaveChanged) {
                this.processSubscriptionData();
                this.processSubscribers();
                this.onSubscriptionsUpdate(this.activeSubscriptions);
                this.subscriptionsHaveChanged = false;
            }
        }, options.subscribersProcessingFrequency);
    }

    /**
     * updateSubscribers - update the internal subscribers list, based on which actual subscriptions are updated
     */
    updateSubscribers(subscribersByTopic) {
        this.subscribersByTopic = subscribersByTopic;
        this.subscriptionsHaveChanged = true;
    }


    /**
     * processSubscribers
     * - turn subscribers into subscriptions, unsub subscriptions that are no longer in use
     * - only updates activeSubscriptions, reacts to subscribersByTopic
     */
    processSubscribers() {
        // Relevant Props
        const { options, subscribersByTopic, activeSubscriptions } = this;

        // No processing if disconnected, void activeSubscriptions
        const session = getSession();
        if (!session) {
            this.activeSubscriptions = {};
            this.subscriptionsHaveChanged = true;
            return;
        }

        // Relevant Opts
        const {
            validateInitialDump, isErrorInitialDump, parseInitialDump,
            validateUpdate, isErrorUpdate, parseUpdate, isSubscriptionError,
            mergeUpdates, uniqueKey, initialDumpEndpoint, unregisterEndpoint,
            retryAfter,
        } = options;

        const now = Date.now();

        // Register Topics for Subscribers Without a Topic
        for (const topic of Object.keys(subscribersByTopic)) {
            if (
                !activeSubscriptions[topic] ||
                activeSubscriptions[topic] &&
                    activeSubscriptions[topic].nextRetry !== false &&
                    activeSubscriptions[topic].nextRetry < now &&
                    !activeSubscriptions[topic].isBusy
            ) {
                // overwrite existing subscription
                activeSubscriptions[topic] = {
                    registration: null,
                    topic,

                    currentState: null,
                    pendingUpdates: [],
                    pendingErrors: [],

                    acceptsUpdates: false, // means we can't push updates to pendingUpdates
                    suppliesData: false, // whether this subscription is an active data supplier; will otherwise not be processed
                    isBusy: true, // means register/unregister operations are locked

                    nextRetry: false,
                };

                this.subscriptionsHaveChanged = true;

                // we can now perform the registration
                session.register(topic, (args, kwargs, details) => {
                    const response = { args, kwargs, details };
                    // Updates Handler
                    if (activeSubscriptions[topic] && activeSubscriptions[topic].acceptsUpdates) {
                        if (!validateUpdate(response, uniqueKey)) {
                            if (isErrorUpdate(response, uniqueKey)) {
                                activeSubscriptions[topic].pendingErrors.push(response);
                                this.subscriptionsHaveChanged = true;
                            } else {
                                throw new Error(`Subscription update validation failed for ${topic}.
                                    This is very likely to be the result of a breaking change in the API`);
                            }
                        } else {
                            if (!activeSubscriptions[topic].suppliesData) {
                                activeSubscriptions[topic].pendingUpdates.push(parseUpdate(response, uniqueKey));
                            } else {
                                activeSubscriptions[topic].currentState = mergeUpdates(activeSubscriptions[topic].currentState, [parseUpdate(response, uniqueKey)], uniqueKey);
                            }
                            this.subscriptionsHaveChanged = true;
                        }
                    }
                    return new Promise(resolve => resolve(''));
                }, {}).then((registration) => {
                    // Registration Successful - save it; we now accept updates - but activeSubscription is still busy
                    activeSubscriptions[topic].registration = registration;
                    activeSubscriptions[topic].acceptsUpdates = true;
                    this.subscriptionsHaveChanged = true;

                    // Registration Ditching Mechanism
                    const ditchRegistration = (retry = true) => {
                        activeSubscriptions[topic].acceptsUpdates = false;
                        activeSubscriptions[topic].suppliesData = false;
                        activeSubscriptions[topic].pendingUpdates = [];
                        activeSubscriptions[topic].currentState = null;
                        this.subscriptionsHaveChanged = true;

                        // session.call(unregisterEndpoint, [], { registrationId: registration.id }, {}).then(response => {
                        //     if (!response || !response.kwargs || !response.kwargs.registrationId) {
                        //         return disconnect();
                        //     }

                            return session.unregister(registration).then(() => {
                                activeSubscriptions[topic].nextRetry = retry && Date.now() + retryAfter || false;
                                activeSubscriptions[topic].isBusy = false;
                                activeSubscriptions[topic].registration = null;
                                this.subscriptionsHaveChanged = true;
                            }, () => {
                                // Ditch entire connetion on error
                                disconnect();
                            });
                        // }, () => disconnect());
                    };

                    // Call initialDump Endpoint
                    session.call(initialDumpEndpoint, [], { topic }, {}).then((response) => {
                        if (!validateInitialDump(response, uniqueKey)) {
                            ditchRegistration(false);
                            if (isErrorInitialDump(response, uniqueKey)) {
                                activeSubscriptions[topic].pendingErrors.push(response);
                                this.subscriptionsHaveChanged = true;
                            } else {
                                throw new Error(`Initial Dump validation failed for ${topic}.
                                    This is very likely to be the result of a breaking change in the API`);
                            }
                        } else {
                            activeSubscriptions[topic].currentState = mergeUpdates(parseInitialDump(response, uniqueKey), activeSubscriptions[topic].pendingUpdates, uniqueKey);
                            activeSubscriptions[topic].pendingUpdates = [];
                            activeSubscriptions[topic].isBusy = false;
                            activeSubscriptions[topic].suppliesData = true;
                            this.subscriptionsHaveChanged = true;
                        }
                    }, (response) => {
                        const errorCode = response && response.error || false;
                        switch (errorCode) {
                            // Actual Error From The WAMP Router
                            case 'wamp.error.no_business_server':
                            case 'wamp.no.backend':
                                ditchRegistration();
                                break;
                            case 'wamp.error.no_such_procedure':
                            case 'om.rpc.exception':
                            default:
                                ditchRegistration(false);
                                throw new Error(`Initial dump endpoint ${initialDumpEndpoint} failed or was not found on the server.
                                    This is very likely to be the result of a breaking change in the API`);
                        }
                    });
                }, (response) => {
                    if (isSubscriptionError(response)) {
                        activeSubscriptions[topic].isBusy = false;
                        activeSubscriptions[topic].registration = null;
                        activeSubscriptions[topic].pendingErrors.push(response);
                        this.subscriptionsHaveChanged = true;
                    } else {
                        const errorCode = response && response.error || false;
                        switch (errorCode) {
                            // Actual Error From The WAMP Router
                            case 'wamp.error.no_such_procedure':
                            case 'om.rpc.exception':
                                throw new Error(`Initial dump endpoint ${initialDumpEndpoint} was not found on the server.
                                    This is very likely to be the result of a breaking change in the API`);
                            // Retry registration later
                            case 'wamp.error.no_business_server':
                            case 'wamp.no.backend':
                            default:
                                activeSubscriptions[topic].nextRetry = Date.now() + retryAfter;
                                activeSubscriptions[topic].isBusy = false;
                                this.subscriptionsHaveChanged = true;
                                break;
                        }
                    }
                });
            }
        }

        // Unregister Topics Without Subscribers
        for (const topic of Object.keys(activeSubscriptions)) {
            if (
                !subscribersByTopic[topic] &&
                !activeSubscriptions[topic].isBusy &&
                activeSubscriptions[topic].nextRetry === false
            ) {
                if (!activeSubscriptions[topic].registration) {
                    delete activeSubscriptions[topic];
                    this.subscriptionsHaveChanged = true;
                } else {
                    activeSubscriptions[topic].isBusy = true;
                    activeSubscriptions[topic].acceptsUpdates = false;
                    activeSubscriptions[topic].suppliesData = false;
                    activeSubscriptions[topic].currentState = null;
                    this.subscriptionsHaveChanged = true;

                    // session.call(unregisterEndpoint, [], { registrationId: activeSubscriptions[topic].registration.id }, {}).then(response => {
                    //     if (!response || !response.kwargs || !response.kwargs.registrationId) {
                    //         return disconnect();
                    //     }

                        return session.unregister(activeSubscriptions[topic].registration).then(() => {
                            delete activeSubscriptions[topic];
                            this.subscriptionsHaveChanged = true;
                        }, () => {
                            disconnect();
                        });
                    // }, () => disconnect());
                }
            }
        }
        this.subscriptionsHaveChanged = true;
    }


    /**
     * Send Updates To Subscribers
     * - only updates subscribersByTopic, calls handlers on subscribers, reacts to activeSubscriptions
     * - gets called every time activeSubscriptions changes
     * - merges updates
     * - indexes data
     */
    processSubscriptionData() {
        // Relevant Options
        const { subscribersByTopic, activeSubscriptions } = this;

        // Iterate Subscribers by Topic
        for (const topic of Object.keys(subscribersByTopic)) {
            for (const subscriber of subscribersByTopic[topic]) {
                // call onPending
                if (
                    typeof subscriber.isPending === 'undefined' || // subscriber has no isPending field set
                    !subscriber.isPending && (!activeSubscriptions[topic] || activeSubscriptions[topic] && !activeSubscriptions[topic].suppliesData)
                ) {
                    subscriber.isPending = true;
                    subscriber.onPending();
                }

                // Call onError if there were errors
                if (activeSubscriptions[topic] && activeSubscriptions[topic].pendingErrors && activeSubscriptions[topic].pendingErrors.length) {
                    subscriber.onError(activeSubscriptions[topic].pendingErrors);
                }

                // Call onUpdate if we have data
                if (activeSubscriptions[topic]) {
                    if (activeSubscriptions[topic].currentState !== subscriber.lastUpdate) {
                        subscriber.lastUpdate = activeSubscriptions[topic].currentState;
                        subscriber.onUpdate(activeSubscriptions[topic].currentState);
                    }
                } else if (subscriber.lastUpdate !== null) {
                    subscriber.lastUpdate = null;
                    subscriber.onUpdate(null);
                }

                // Call onComplete if ready
                if (activeSubscriptions[topic] && activeSubscriptions[topic].suppliesData && subscriber.isPending) {
                    subscriber.isPending = false;
                    subscriber.onCompletion();
                }
            }

            // We're done with errors, clear the errors queue
            if (activeSubscriptions[topic] && activeSubscriptions[topic].pendingErrors && activeSubscriptions[topic].pendingErrors.length) {
                activeSubscriptions[topic].pendingErrors = [];
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/OMFECore/enhanced-webapi/api-provisioning/SubscribersProcessor.js